
'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import Link from 'next/link';
import { Cookie, Settings } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useCookieConsent } from '@/context/CookieConsentContext';

export default function CookieBanner() {
  const { 
    consent, 
    hasMadeChoice, 
    acceptAll, 
    declineAll, 
    savePreferences, 
    isSettingsOpen, 
    openSettings,
    closeSettings 
  } = useCookieConsent();
  
  const [showBanner, setShowBanner] = useState(false);
  const [tempPreferences, setTempPreferences] = useState({ advertising: consent.advertising });

  useEffect(() => {
    setShowBanner(!hasMadeChoice);
  }, [hasMadeChoice]);

  useEffect(() => {
    // Reset temporary preferences to the actual consent when the dialog is opened
    if (isSettingsOpen) {
      setTempPreferences({ advertising: consent.advertising });
    }
  }, [consent, isSettingsOpen]);

  const handleSave = () => {
    savePreferences(tempPreferences);
    closeSettings();
  };

  const handleAcceptAll = () => {
    acceptAll();
  }

  const handleDeclineAll = () => {
    declineAll();
  }

  return (
    <>
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4">
          <Card className="max-w-4xl mx-auto shadow-2xl">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <Cookie className="h-8 w-8 text-primary flex-shrink-0" />
                <div className="flex-grow text-sm text-center md:text-left">
                  <h3 className="font-bold text-base">Cookie-instellingen</h3>
                  <p className="text-muted-foreground mt-1">
                    Wij gebruiken cookies om uw ervaring te verbeteren en voor advertentiedoeleinden. Door te accepteren gaat u akkoord met ons{' '}
                    <Link href="/privacy" className="underline hover:text-primary">
                      privacybeleid
                    </Link>.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 mt-4 md:mt-0 flex-shrink-0 w-full sm:w-auto">
                  <Button onClick={handleAcceptAll} className="flex-1 sm:flex-initial">Accepteren</Button>
                  <Button onClick={openSettings} variant="outline" className="flex-1 sm:flex-initial">
                      <Settings className="mr-2 h-4 w-4" />
                      Aanpassen
                  </Button>
                  <Button onClick={handleDeclineAll} variant="secondary" className="flex-1 sm:flex-initial">Weigeren</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={isSettingsOpen} onOpenChange={(open) => !open && closeSettings()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cookievoorkeuren aanpassen</DialogTitle>
            <DialogDescription>
              Beheer hier uw cookie-instellingen. Strikt noodzakelijke cookies kunnen niet worden uitgeschakeld.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label>Noodzakelijke Cookies</Label>
                <p className="text-xs text-muted-foreground">
                  Deze cookies zijn essentieel voor de basisfunctionaliteit van de site, zoals het onthouden van uw cookie-keuze.
                </p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="advertising-cookies">Advertentiecookies</Label>
                 <p className="text-xs text-muted-foreground">
                  Deze cookies worden gebruikt om advertenties relevanter voor u te maken. Ingeschakeld, kunnen partners zoals Google deze gebruiken.
                </p>
              </div>
              <Switch
                id="advertising-cookies"
                checked={tempPreferences.advertising}
                onCheckedChange={(checked) => setTempPreferences({ ...tempPreferences, advertising: checked })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeSettings}>Annuleren</Button>
            <Button onClick={handleSave}>Voorkeuren opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
